import React, { useState, useContext, useEffect } from "react";
import TaskAssignMembers from "../TaskAssignMember/TaskAssignMembers";
import { LoginContext } from "../../services/api/authentication-api";
import "./TaskAssignedMemberWrapper.scss";
import PropTypes from "prop-types";

const TaskAssignMemberWrapper = ({
  isCreate,
  task,
  taskMembers,
  projectMembers,
  selectedMembersEdit,
  selectedMembersView,
  selectedMembersApprove,
  setSelectedMembersEdit,
  setSelectedMembersView,
  setSelectedMembersApprove,
  selectedGroupEdit,
  selectedGroupView,
  selectedGroupApprove,
  setSelectedGroupEdit,
  setSelectedGroupView,
  setSelectedGroupApprove,
  selectedMembersOwner,
  setSelectedMembersOwner,
  selectedGroupsOwner,
  setSelectedGroupsOwner,
  workItemType,
  groupOwner,
  disabled,
}) => {
  const context = useContext(LoginContext);
  const [permissionTaskMembers, setPermissionTaskMembers] = useState([]);
  const [taskAssigned, setTaskAssigned] = useState(false);
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [assignedOwner, setAssignedOwner] = useState({});
  const [assignedOtherDepMembers, setAssignedOtherDepMembers] = useState([]);
  const [filterAssignedMembers, setFilterAssignedMembers] = useState([]);
  const [filteredOtherDepMembers, setFilteredOtherDepMembers] = useState([]);

  useEffect(() => {
    getAllAssignedMembers(taskMembers, projectMembers);
  }, [taskMembers, projectMembers, taskAssigned, groupOwner]);

  const getAllAssignedMembers = (taskMembers, otherProjMembers) => {
    let currMembers = [];
    let filterMembersNames = [];
    let otherFilterMembersNames = [];
    let permissionMembers = [];
    let otherMembers = [];
    taskMembers.forEach(function (member) {
      if (!isCreate && member.User.Id == task.CreatedById && !member.IsGroup) {
        setAssignedOwner(member.User);
      }
      filterMembersNames.push(member.User.Name);
      currMembers.push(member.User);
      permissionMembers.push({
        Id: member.UserId,
        canApprove: member.CanApprove,
        canEdit: member.CanEdit || member.IsAssigned,
        canReassign: member.CanReassign,
        canView: member.CanView,
        IsGroup: member.IsGroup,
        // Archived/moved assignees are absent from the assignable pool; carry the
        // flag and a render-ready object so the chips can fall back to it (TAD-5898).
        Archived: member.IsGroup ? member.Archived === true : member.User?.Archived === true,
        Member: member.IsGroup
          ? { ...member, Archived: member.Archived === true, Members: member.GroupMembers || [] }
          : member.User,
      });
    });

    otherProjMembers.forEach(function (member) {
      if (isCreate && member.User.Id == context.userID && !member.IsGroup) {
        setAssignedOwner(member.User);
      }
      otherFilterMembersNames.push(member.User.Name);
      if (member?.Group?.Id > 0) {
        member.Group.IsGroup = true;
        otherMembers.push(member.Group);
      } else otherMembers.push(member.User);
    });

    let uniqueMembers = removeDuplicates(currMembers.filter(x => !x.IsGroup), "Id");
    let uniqueGroups = removeDuplicates(currMembers.filter(x => x.IsGroup), "Id");
    let uniqueOtherMembers = removeDuplicates(otherMembers.filter(x => !x.IsGroup), "Id");
    let uniqueOtherGroups = removeDuplicates(otherMembers.filter(x => x.IsGroup), "Id");
    let uniquePermissionMembers = removeDuplicates(permissionMembers.filter(x => !x.IsGroup), "Id");
    let uniquePermissionGroups = removeDuplicates(permissionMembers.filter(x => x.IsGroup), "Id");
    let uniquePermissions = [...uniquePermissionMembers, ...uniquePermissionGroups];
    setAssignedMembers([...uniqueMembers, ...uniqueGroups]);
    setAssignedOtherDepMembers([...uniqueOtherMembers, ...uniqueOtherGroups]);
    setPermissionTaskMembers([...uniquePermissions]);
    setFilterAssignedMembers([...new Set(filterMembersNames)]);
    setFilteredOtherDepMembers([...new Set(otherFilterMembersNames)]);
  };

  const removeDuplicates = (originalArray, prop) => {
    let newArray = [];
    let lookupObject = {};
    for (let i in originalArray) {
      lookupObject[originalArray[i][prop]] = originalArray[i];
    }

    for (let i in lookupObject) {
      newArray.push(lookupObject[i]);
    }
    return newArray;
  };

  const addMembersInForm = (permission, id, IsGroup = false) => {
    if (IsGroup) {
      let addedMembers = [];
      if (permission === "editor") {
        addedMembers = [...selectedGroupEdit];
      } else if (permission === "viewer") {
        addedMembers = [...selectedGroupView];
      } else if (permission === "approver") {
        addedMembers = [...selectedGroupApprove];
      }

      const indexMembers = addedMembers.indexOf(id);
      if (indexMembers > -1) {
        addedMembers.splice(indexMembers, 1);
      }
      addedMembers.push(id);

      if (permission === "editor") {
        setSelectedGroupEdit(addedMembers);
      } else if (permission === "viewer") {
        setSelectedGroupView(addedMembers);
      } else if (permission === "approver") {
        setSelectedGroupApprove(addedMembers);
      } else if (permission === "owner") {
        setSelectedGroupsOwner([id]);
      }
    } else {
      let addedMembers = [];
      if (permission === "editor") {
        addedMembers = [...selectedMembersEdit];
      } else if (permission === "viewer") {
        addedMembers = [...selectedMembersView];
      } else if (permission === "approver") {
        addedMembers = [...selectedMembersApprove];
      }

      const indexMembers = addedMembers.indexOf(id);
      if (indexMembers > -1) {
        addedMembers.splice(indexMembers, 1);
      }
      addedMembers.push(id);

      if (permission === "editor") {
        setSelectedMembersEdit(addedMembers);
      } else if (permission === "viewer") {
        setSelectedMembersView(addedMembers);
      } else if (permission === "approver") {
        setSelectedMembersApprove(addedMembers);
      } else if (permission === "owner") {
        setSelectedMembersOwner([id]);
        setAssignedOwner(projectMembers.find((member) => member?.User?.Id === id)?.User);
      }
    }
  };

  const removeMembersInForm = (permission, id, IsGroup = false) => {
    if (IsGroup) {
      let updatedMembers = [];
      if (permission === "editor") {
        updatedMembers = [...selectedGroupEdit];
      } else if (permission === "viewer") {
        updatedMembers = [...selectedGroupView];
      } else if (permission === "approver") {
        updatedMembers = [...selectedGroupApprove];
      }

      updatedMembers = updatedMembers.filter((member) => member !== id);

      if (permission === "editor") {
        setSelectedGroupEdit(updatedMembers);
      } else if (permission === "viewer") {
        setSelectedGroupView(updatedMembers);
      } else if (permission === "approver") {
        setSelectedGroupApprove(updatedMembers);
      }
    } else {
      let updatedMembers = [];
      if (permission === "editor") {
        updatedMembers = [...selectedMembersEdit];
      } else if (permission === "viewer") {
        updatedMembers = [...selectedMembersView];
      } else if (permission === "approver") {
        updatedMembers = [...selectedMembersApprove];
      }

      updatedMembers = updatedMembers.filter((member) => member !== id);

      if (permission === "editor") {
        setSelectedMembersEdit(updatedMembers);
      } else if (permission === "viewer") {
        setSelectedMembersView(updatedMembers);
      } else if (permission === "approver") {
        setSelectedMembersApprove(updatedMembers);
      }
    }
  };

  const PermissionCheck = (permission) => {
    switch (permission) {
      case "owner":
        return task.AssignedMemberPermission?.CanChangeOwner
      case "editor":
        return task.AssignedMemberPermission?.CanChangeEditor
      case "viewer":
        return task.AssignedMemberPermission?.CanChangeViewer
      case "approver":
        return task.AssignedMemberPermission?.CanChangeApprover
      default:
        return false;
    }
  }

  return (
    <div className="TaskAssignMembersWrapper">
      <div className="wrapperHeader">Assign Members <span className="red">*</span></div>
      <div className="memberBox">
        <TaskAssignMembers
          assignedMembers={assignedOtherDepMembers}
          assignedOwner={assignedOwner}
          filteredAssignedMembers={filteredOtherDepMembers}
          addMembersInForm={addMembersInForm}
          removeMembersInForm={removeMembersInForm}
          permissionTaskMembers={permissionTaskMembers}
          task={task}
          currUserId={context?.userID}
          ownerGroup={groupOwner}
          isDisabled={
            assignedOtherDepMembers?.length === 0 
            || (workItemType != null && workItemType.IsUsersEnabled === false) 
            || disabled
          }
          CanChangeApprover={isCreate || PermissionCheck("approver")}
          CanChangeEditor={isCreate || PermissionCheck("editor")}
          CanChangeViewer={isCreate || PermissionCheck("viewer")}
          CanChangeOwner={isCreate || PermissionCheck("owner")}
        />
      </div>
    </div>
  );
};

TaskAssignMemberWrapper.propTypes = {
  isCreate: PropTypes.bool,
  task: PropTypes.object,
  taskMembers: PropTypes.array,
  projectMembers: PropTypes.array,
  selectedMembersEdit: PropTypes.array,
  selectedMembersView: PropTypes.array,
  selectedMembersApprove: PropTypes.array,
  setSelectedMembersEdit: PropTypes.func,
  setSelectedMembersView: PropTypes.func,
  setSelectedMembersApprove: PropTypes.func,
  selectedGroupEdit: PropTypes.array,
  selectedGroupView: PropTypes.array,
  selectedGroupApprove: PropTypes.array,
  setSelectedGroupEdit: PropTypes.func,
  setSelectedGroupView: PropTypes.func,
  setSelectedGroupApprove: PropTypes.func,
  selectedMembersOwner: PropTypes.array,
  setSelectedMembersOwner: PropTypes.func,
  selectedGroupsOwner: PropTypes.array,
  setSelectedGroupsOwner: PropTypes.func,
  groupOwner: PropTypes.object,
  disabled: PropTypes.bool,
  workItemType: PropTypes.shape({
    IsUsersEnabled: PropTypes.bool,
  }),
};
export default TaskAssignMemberWrapper;
